import {
  index,
  integer,
  jsonb,
  pgTable,
  text,
  timestamp,
  uuid,
} from "drizzle-orm/pg-core";

// ---------------------------------------------------------------------------
// Email Drafts (AI-013) — canonical schema for the `email_drafter` launch
// agent (`packages/ai/src/agents/launch-agents.ts`). The agent's
// `email.draft` MCP tool POSTs into `/email/drafts`; this schema is the
// persistence target the route handler writes.
//
// This file is the SINGLE canonical location for the email_drafts table.
// PR #1755 introduced a parallel definition inside `./email.ts`; the dup
// was removed and the union of fields preserved here.
//
// Key invariants:
//   * Drafts are tenant-scoped — every read/write MUST filter by tenant_id.
//   * Drafts start as `pending_approval` and CANNOT bypass the pre-send
//     safety pipeline (suppression / bounce-hash / reputation / content
//     scrub from `packages/email-core/src/services/pre-send-checks.ts`).
//     The pipeline result is captured in `preSendChecksJson` at write time
//     so the approver can read what was checked, when, and the outcome.
//   * Status transitions are write-only forward:
//       pending_approval -> approved -> queued -> sent
//       pending_approval -> rejected
//       any -> failed
//     The route handler enforces this; this schema does not encode it as
//     a CHECK constraint (kept portable across Hetzner + Neon drivers).
//   * `authorAgentTaskId` links the draft to the originating `AgentTask`
//     (AI-005) so DSAR export + retention can walk agent -> draft -> send.
//   * `authorUserId` captures the human user who owns the originating
//     agent task — used by the self-approval block in
//     `workers/api-email/src/routes/drafts.ts`.
// ---------------------------------------------------------------------------

// NOTE: imported lazily to avoid pulling email.ts into the email-drafts
// module graph (which would re-introduce the cycle the dedup removed).
// The reference uses a forward callback so `sentEmails` is resolved at
// table-build time, not at module-load time.
import { sentEmails } from "./email";

export const EMAIL_DRAFT_STATUSES = [
  "pending_approval",
  "approved",
  "rejected",
  "queued",
  "sent",
  "failed",
] as const;

export type EmailDraftStatus = (typeof EMAIL_DRAFT_STATUSES)[number];

export interface EmailDraftPreSendChecks {
  passed: boolean;
  // Per-check outcome — keys mirror pre-send-checks pipeline names.
  results: Record<
    string,
    {
      status: "pass" | "block" | "warn";
      reason?: string;
    }
  >;
  // Wall-clock when checks ran. Stored as ISO-8601 string so JSON round-trips
  // are stable across Postgres + Neon drivers.
  checkedAt: string;
}

export const emailDrafts = pgTable(
  "email_drafts",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    // Originating launch-agent task (AI-005). Nullable so manual drafts can
    // share the table once a /drafts UI lands — not just AI-authored ones.
    authorAgentTaskId: uuid("author_agent_task_id"),
    // Human user who owns the originating agent task. Powers the
    // self-approval block (a user MUST NOT approve their own draft).
    authorUserId: uuid("author_user_id"),
    // Recipient email. Free-form text keeps the schema portable; validation
    // happens at the route handler boundary.
    toEmail: text("to_email").notNull(),
    subject: text("subject").notNull(),
    bodyHtml: text("body_html").notNull(),
    status: text("status").notNull().default("pending_approval"),
    // jsonb is intentionally untyped at the column level — consumers
    // (route handlers, retention enforcer, DSAR exporter) carry their own
    // narrower types. The canonical `EmailDraftPreSendChecks` interface
    // remains exported for callers that want strict typing.
    preSendChecksJson: jsonb("pre_send_checks_json"),
    // Reason captured when an approver rejects, or when pre-send checks
    // auto-rejected the draft. Free-form, optional.
    rejectedReason: text("rejected_reason"),
    // Approver actor id (auth users live outside the tenant DB so we keep
    // this as a uuid rather than an FK).
    approvedByUserId: uuid("approved_by_user_id"),
    approvedAt: timestamp("approved_at", { withTimezone: true }),
    queuedAt: timestamp("queued_at", { withTimezone: true }),
    sentAt: timestamp("sent_at", { withTimezone: true }),
    // Walk-forward link to the queue/send path: once an approved draft is
    // handed to the transactional send pipeline, the resulting `sent_emails`
    // row is captured here so DSAR/audit can trace draft -> send.
    sentEmailId: uuid("sent_email_id").references(() => sentEmails.id, {
      onDelete: "set null",
    }),
    // Monotonic version for optimistic concurrency on status flips. Route
    // handlers do `UPDATE ... WHERE id = ? AND version = ?` then increment.
    version: integer("version").notNull().default(1),
    createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow().notNull(),
  },
  (table) => [
    // Reviewer queue: "show me everything pending in this tenant, oldest first".
    index("idx_email_drafts_tenant_status").on(
      table.tenantId,
      table.status,
      table.createdAt,
    ),
    // Audit walk-back: agent -> drafts.
    index("idx_email_drafts_agent_task").on(table.tenantId, table.authorAgentTaskId),
  ],
);

export type EmailDraftRecord = typeof emailDrafts.$inferSelect;
export type EmailDraftInsert = typeof emailDrafts.$inferInsert;
